import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Phone, Clock, CheckCircle2, XCircle, Calendar as CalIcon, AlertTriangle, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { parseLocalIso } from "@/lib/dt";

/**
 * Follow-ups a realizar — página dedicada onde o Atendimento/Gestor vê
 * os follow-ups manuais criados no fim de cada chamada (não-atendida ou
 * reagendada). Agrupados por prazo. Botão "Concluir" fecha o item em
 * Caring + Zoho task.
 *
 * scope=mine  → só os follow-ups criados pelo utilizador actual
 * scope=all   → visível a Admin/Direção/DGO (todos os operadores)
 */
export default function Followups() {
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("mine");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scheduled-followups/pending?scope=${scope}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erro a carregar");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const complete = async (fu, outcome, note) => {
    setBusy((b) => ({ ...b, [fu.id]: true }));
    try {
      await api.post(`/leads/${fu.lead_id}/scheduled-followups/${fu.id}/complete`, {
        outcome, note,
      });
      toast.success("Follow-up concluído");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erro a concluir");
    } finally {
      setBusy((b) => ({ ...b, [fu.id]: false }));
    }
  };

  const cancel = async (fu) => {
    if (!window.confirm(`Cancelar o follow-up de ${fu.lead_name}? (não fica marcado como concluído)`)) return;
    setBusy((b) => ({ ...b, [fu.id]: true }));
    try {
      await api.post(`/leads/${fu.lead_id}/scheduled-followups/${fu.id}/cancel`);
      toast.success("Follow-up cancelado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erro");
    } finally {
      setBusy((b) => ({ ...b, [fu.id]: false }));
    }
  };

  const canSeeAll = ["admin", "direcao", "dgo"].includes(me?.role);
  const buckets = data?.buckets || {};
  const labels = data?.labels || {};
  const orderedBuckets = ["atrasado", "hoje", "amanha", "esta_semana", "duas_semanas", "mais_tarde", "sem_data"];

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto space-y-8" data-testid="followups-root">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Operacional</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Follow-ups a realizar</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
            Chamadas de retorno agendadas — criadas automaticamente quando o cliente não atende ou pede para ligar mais tarde.
          </p>
        </div>
        {canSeeAll && (
          <div className="inline-flex items-center gap-1 bg-card border border-border rounded-full p-1">
            <button
              onClick={() => setScope("mine")}
              data-testid="scope-mine"
              className={`text-xs px-4 py-1.5 rounded-full font-semibold transition-colors ${scope === "mine" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted/40"}`}
            >Meus</button>
            <button
              onClick={() => setScope("all")}
              data-testid="scope-all"
              className={`text-xs px-4 py-1.5 rounded-full font-semibold transition-colors ${scope === "all" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted/40"}`}
            >Todos</button>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" /> A carregar…
        </div>
      )}

      {!loading && data && data.total === 0 && (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="empty-state">
          <CheckCircle2 className="w-12 h-12 text-[hsl(var(--success))] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-ui text-lg text-foreground">Sem follow-ups pendentes</div>
          <div className="text-sm text-muted-foreground mt-1">
            Novos follow-ups aparecem aqui quando terminar uma chamada com "Cliente não atendeu" ou "Reagendar".
          </div>
        </div>
      )}

      {!loading && data && data.total > 0 && (
        <div className="space-y-6" data-testid="followups-list">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-foreground text-background text-xs font-bold">{data.total}</span>
            follow-up(s) pendente(s)
          </div>

          {orderedBuckets.map((key) => {
            const items = buckets[key] || [];
            if (items.length === 0) return null;
            const isOverdue = key === "atrasado";
            return (
              <section key={key} data-testid={`bucket-${key}`}>
                <div className={`flex items-center gap-2 mb-3 ${isOverdue ? "text-[hsl(var(--danger))]" : "text-foreground"}`}>
                  {isOverdue ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4 text-foreground" />}
                  <h2 className="font-ui text-lg">{labels[key] || key}</h2>
                  <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${isOverdue ? "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]" : "bg-muted text-foreground"}`}>
                    {items.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {items.map((fu) => (
                    <FollowupCard key={fu.id} fu={fu} busy={busy[fu.id]} onComplete={complete} onCancel={cancel} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FollowupCard({ fu, busy, onComplete, onCancel }) {
  const [openComplete, setOpenComplete] = useState(false);
  const [note, setNote] = useState("");
  const dt = parseLocalIso(fu.scheduled_for);

  return (
    <div className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-start gap-4" data-testid={`fu-card-${fu.id}`}>
      <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
        {fu.outcome === "no_answer" ? <Phone className="w-5 h-5 text-[hsl(var(--danger))]" /> : <CalIcon className="w-5 h-5 text-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <Link to={`/leads/${fu.lead_id}`} className="font-semibold text-foreground hover:text-foreground" data-testid="fu-lead-link">
            {fu.lead_name}
          </Link>
          {fu.lead_phone && <span className="text-xs text-muted-foreground">· {fu.lead_phone}</span>}
          {fu.lead_email && <span className="text-xs text-muted-foreground">· {fu.lead_email}</span>}
        </div>
        <div className="text-sm text-foreground/85 mb-1"><b>{fu.reason}</b></div>
        {fu.note && <div className="text-[13px] text-muted-foreground italic mb-1">"{fu.note}"</div>}
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
          {dt && <span><Clock className="w-3 h-3 inline mr-1" />{dt.toLocaleString("pt-PT")}</span>}
          <span><UserIcon className="w-3 h-3 inline mr-1" />criado por {fu.created_by_name}</span>
          {fu.zoho_task_id && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-foreground border border-border font-semibold uppercase tracking-widest text-[9px]">Zoho Task</span>}
        </div>

        {openComplete && (
          <div className="mt-3 p-3 bg-muted/40 border border-border rounded-lg space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Nota de conclusão (opcional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
              data-testid={`fu-completion-note-${fu.id}`}
              placeholder="Ex.: falei com o cliente, agendou RT p/ 22/07"
              className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm min-h-[60px] focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setOpenComplete(false); setNote(""); }}
                className="text-xs bg-card hover:bg-muted/40 border border-border rounded-full px-3 py-1.5"
              >Cancelar</button>
              <button
                onClick={() => onComplete(fu, "success", note)}
                disabled={busy}
                data-testid={`btn-confirm-complete-${fu.id}`}
                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-3 py-1.5 font-semibold disabled:opacity-50"
              >Confirmar conclusão</button>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 items-end">
        {!openComplete && (
          <>
            <button
              onClick={() => setOpenComplete(true)}
              disabled={busy}
              data-testid={`btn-open-complete-${fu.id}`}
              className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Concluir
            </button>
            <button
              onClick={() => onCancel(fu)}
              disabled={busy}
              data-testid={`btn-cancel-${fu.id}`}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-[hsl(var(--danger))]"
              title="Cancelar (sem marcar como concluído)"
            >
              <XCircle className="w-3 h-3" /> Cancelar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
